import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ScreenerSetupMatrix from './ScreenerSetupMatrix';
import { apiService, SetupMatrixResponse } from '../../services/api';

interface SetupMatrixPopoverProps {
  symbol: string;
  currentCell: string | null;
  /** Element the popover is anchored to (desktop). On narrow screens it opens as a bottom sheet. */
  anchor: HTMLElement | null;
  onClose: () => void;
  contract: 'call' | 'put';
}

/**
 * The per-stock setup matrix (moved out of ScoreBreakdown's SetupSummary).
 * Lazy-loads the matrix on open: prefetching per card used to 429 the screener.
 * Rendered in a portal to escape the card's overflow; closes on Escape or outside click.
 */
const SetupMatrixPopover: React.FC<SetupMatrixPopoverProps> = ({ symbol, currentCell, anchor, onClose, contract }) => {
  const [matrix, setMatrix] = useState<SetupMatrixResponse | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const narrow = typeof window !== 'undefined' && window.innerWidth < 480;

  useEffect(() => {
    let cancelled = false;
    apiService.getStockSetupMatrix(symbol)
      .then((res) => {
        if (cancelled) return;
        setMatrix(res);
      })
      .catch((err) => console.error('Failed to load setup matrix', err));
    return () => { cancelled = true; };
  }, [symbol]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popupRef.current && !popupRef.current.contains(t) && !(anchor && anchor.contains(t))) onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchor, onClose]);

  // Position from the popover's real rendered height so it always stays on screen:
  // below the link when it fits, otherwise shifted up; scrolls if the window is tiny.
  const [top, setTop] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (narrow || !anchor || !popupRef.current) return;
    const rect = anchor.getBoundingClientRect();
    const h = popupRef.current.offsetHeight;
    let t = rect.bottom + 4;
    if (t + h > window.innerHeight - 8) t = Math.max(8, window.innerHeight - h - 8);
    setTop(t);
  }, [anchor, narrow, matrix]);

  let style: React.CSSProperties;
  if (narrow || !anchor) {
    style = { position: 'fixed', left: 8, right: 8, bottom: 8, maxHeight: 'calc(100vh - 16px)', overflowY: 'auto' };
  } else {
    const rect = anchor.getBoundingClientRect();
    const w = 460;
    let left = Math.min(rect.left, window.innerWidth - w - 8);
    if (left < 8) left = 8;
    style = {
      position: 'fixed',
      left,
      top: top ?? rect.bottom + 4,
      width: w,
      maxHeight: 'calc(100vh - 16px)',
      overflowY: 'auto',
      visibility: top == null ? 'hidden' : 'visible',
    };
  }

  return createPortal(
    <div
      ref={popupRef}
      className="sc2-matrix-popover"
      role="dialog"
      aria-label={`${symbol} setup matrix`}
      style={style}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="sc2-matrix-head">
        <span>{symbol}'s setups</span>
        <button type="button" onClick={onClose} aria-label="Close setup matrix">×</button>
      </div>
      {matrix ? (
        <ScreenerSetupMatrix
          symbol={symbol}
          matrix={{ ...matrix, current_cell: matrix.current_cell ?? currentCell }}
          contract={contract}
        />
      ) : (
        <div className="sc2-matrix-loading">Loading…</div>
      )}
    </div>,
    document.body,
  );
};

export default SetupMatrixPopover;
